import {
  LinearAudienceConstraint as AudienceConstraint,
  LinearAvoidConstraint as AvoidConstraint,
  LinearBudgetConstraint as BudgetConstraint,
  LinearGlobalSettings as GlobalConstraints,
  LinearSpotConstraint as SpotConstraint
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { demographicKeys } from './linear-optimizations-utils';

// TRPs is restricted to broad household/18+ demos -- a more granular demo doesn't have a TRPs
// figure to target against.
const TRPS_ALLOWED_DEMOGRAPHICS = ['hh', 'm18plus', 'a18plus', 'w18plus'];

// True if any field on `row` other than the excluded keys (and `index`) is meaningfully set.
// Budget/Spot/Avoid all require the constraint to be scoped by *something* beyond min/max values
// or a name -- an unscoped constraint would apply to literally everything, which is never intended.
const hasAnyOtherField = (row: Record<string, unknown>, excludeKeys: string[]): boolean =>
  Object.entries(row).some(
    ([key, value]) =>
      key !== 'index' &&
      !excludeKeys.includes(key) &&
      (Array.isArray(value) ? value.length > 0 : Boolean(value))
  );

export const checkIsBudgetValid = (
  data: Partial<BudgetConstraint>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  if (!data.name) {
    handleInvalidMessage?.('Name is required');
    return false;
  }
  if (
    data.minValue == null &&
    data.maxValue == null &&
    data.minPct == null &&
    data.maxPct == null
  ) {
    handleInvalidMessage?.('At least one of Min $/Min %/Max $/Max % is required');
    return false;
  }
  if (!hasAnyOtherField(data, ['name', 'minValue', 'maxValue', 'minPct', 'maxPct'])) {
    handleInvalidMessage?.(
      'At least one of Creatives/Insights/Length/Network/Avail/Rotation is required'
    );
    return false;
  }
  return true;
};

export const checkIsSpotValid = (
  data: Partial<SpotConstraint>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  if (!data.name) {
    handleInvalidMessage?.('Name is required');
    return false;
  }
  if (data.minValue == null && data.maxValue == null) {
    handleInvalidMessage?.('At least one of Min/Max is required');
    return false;
  }
  if (!hasAnyOtherField(data, ['name', 'minValue', 'maxValue'])) {
    handleInvalidMessage?.(
      'At least one of Creatives/Insights/Length/Network/Avail/Rotation is required'
    );
    return false;
  }
  return true;
};

export const checkIsAudienceValid = (
  data: Partial<AudienceConstraint>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  if (!data.name) {
    handleInvalidMessage?.('Name is required');
    return false;
  }
  if (!data.minValue || data.minValue <= 0) {
    handleInvalidMessage?.('Min Delivery must be greater than 0');
    return false;
  }
  if (!('metric' in data) || !data.metric) {
    handleInvalidMessage?.('Metric is required');
    return false;
  }
  const demoKeys = demographicKeys(data.demographic);
  if (demoKeys.length === 0) {
    handleInvalidMessage?.('At least one demographic is required');
    return false;
  }
  if (
    data.metric === 'TRPs' &&
    (demoKeys.length !== 1 || !TRPS_ALLOWED_DEMOGRAPHICS.includes(demoKeys[0]))
  ) {
    handleInvalidMessage?.(
      `TRPs can only target a single one of: ${TRPS_ALLOWED_DEMOGRAPHICS.join(', ')}`
    );
    return false;
  }
  return true;
};

export const checkIsAvoidValid = (
  data: Partial<AvoidConstraint>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  // Avoid has no `name` field -- it must be scoped by something else entirely.
  if (!hasAnyOtherField(data, [])) {
    handleInvalidMessage?.(
      'At least one of Length/Creatives/Insights/Rated/Network/Avail/Rotation is required'
    );
    return false;
  }
  return true;
};

// `minExtrapolation` sets the start of the Network/Avail/Rotation relaxation window (the optimizer
// widens targeting from this value up to +3) -- a negative start doesn't make sense for that window.
export const checkIsGlobalValid = (
  data: Partial<{ key: keyof GlobalConstraints; value: number }>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  if (data.key === 'minExtrapolation' && Number(data.value) < 0) {
    handleInvalidMessage?.('Invalid input: Relaxation Start must be greater than or equal to 0.');
    return false;
  }
  return true;
};
